import { ChangeEvent, useState } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { useConditionalEffect, usePrevious } from 'react-delta';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';
import CloseIcon from '@mui/icons-material/Close';
import GraphIcon from '@mui/icons-material/TrendingUp';
import DistributionIcon from '@mui/icons-material/Equalizer';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { FacetInfo } from 'common/graphqlTypes';
import { UrlParams } from 'common/types';
import { addTermToFilter, buildParamsWithDeconstructedFilter } from 'common/util/filter/filter';
import { getLinkForRoute } from 'common/util/location';
import {
  draftSearchParamState,
  eventParamsState,
  routeState,
} from 'components/common/Managers/RouteStateManager.state';
import { availableDataSourcesState, viewState } from 'components/common/Managers/ViewStateManager.state';
import { findSchema } from 'schemas';
import { useConditionalStateSync } from 'hooks/useConditionalStateSync';

import Button from 'components/common/Button/Button';
import DataSourceIcon from 'components/common/Icon/DataSourceIcon';
import Toolbar from 'components/common/Toolbar/Toolbar';
import ShadedText from 'components/common/ShadedText/ShadedText';
import Table from 'components/common/Table/Table';
import FacetFilter from 'components/common/FacetFilter/FacetFilter';
import TruncateWithTooltip, { TooltipPlacement } from 'components/common/TruncateWithTooltip/TruncateWithTooltip';
import Link from 'components/common/Link/Link';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import CheckboxRadio from 'components/common/CheckboxRadio/CheckboxRadio';
import { isFacetDetailsVisibleState } from 'components/search/SearchLogsPage/SearchStateManager.state';

import log from 'common/ScalyrLogger';
import { addCommas, roundDownToSigFigs } from 'common/util/number';
import { formatFacetValue, pluralize, quote, unquote } from 'common/util/string';

import './FacetDetails.scss';

type FacetDetailsProps = {
  totalSamples:        number;
  totalMatches:        number;
  facet:               FacetInfo | null;
  close:               () => void;
  getMoreFacetValues:  (facetName: string) => void;
  fetchingMoreValues?: boolean;
  showingMoreValues?:  boolean;
};

/**
 * This component displays a popup next to the sidebar that shows details about the frequency of
 * particular values for a facet.
 *
 * @prop totalSamples          number of matches that were sampled for facet value data
 * @prop totalMatches          number of matches for the search
 * @prop facet                 facet whose details are being shown
 * @prop close                 function to hide this popup
 * @prop getMoreFacetValues    function to fetch additional and more precise value counts
 * @prop fetchingMoreValues    true if a FacetValues request is in flight
 * @prop showingMoreValues     true if facet data is from detailed facetValuesQuery (rather than facetQuery)
 */
function FacetDetails({
  totalSamples,
  totalMatches,
  facet,
  close,
  getMoreFacetValues,
  fetchingMoreValues = false,
  showingMoreValues,
}: FacetDetailsProps): JSX.Element {

  const eventParams = useRecoilValue(eventParamsState);
  const route = useRecoilValue(routeState);
  const isFacetDetailsVisible = useRecoilValue(isFacetDetailsVisibleState);
  // This state is intentionally "stale" while the overlay is closing so that we do not flash the updated state
  const availableDataSources = useConditionalStateSync(
    useRecoilValue(availableDataSourcesState),
    isFacetDetailsVisible,
  );
  const view = useRecoilValue(viewState);
  const [ curLink, setCurLink ] = useState('');
  const [ checkedValues, setCheckedValues ] = useState<string[]>([]);

  // update params using draft params, adding in a new term, and create a target URL with the given term
  const getLink = useRecoilCallback(({ snapshot }) => (field: string, op: string, value: string): string => {
    const draftParams = snapshot.getLoadable(draftSearchParamState).contents as UrlParams;
    const newFilter = addTermToFilter({
      filter: draftParams[K.URL_PARAM.FILTER] || '',
      field,
      op,
      value,
    });
    const params = { ...draftParams, [K.URL_PARAM.FILTER]: newFilter } as UrlParams;
    const view = snapshot.getLoadable(viewState).getValue();
    const adjustedParams = buildParamsWithDeconstructedFilter(params, view);
    return getLinkForRoute(route, eventParams, adjustedParams);
  });

  // reset checkbox state when we change facets
  useConditionalEffect(() => {
    setCheckedValues([]);
  }, facet !== usePrevious(facet));

  if (!facet) {
    return <></>;
  }

  const isSourceSelectorView = view.sources?.sourceField === facet.name;
  let facetValues = facet.values;
  // If this is the SourceSelector view, facetValues should come from availableDataSources
  if (isSourceSelectorView) {
    facetValues = availableDataSources.map(({ dataSourceId }) => {
      const valueFromFacet = facet.values.find(f => unquote(f.value).toLowerCase() === dataSourceId.toLowerCase());
      const notFoundValue = { count: 0, value: quote(dataSourceId) };
      return valueFromFacet ?? notFoundValue;
    });
  }
  // make an API call to get more and better data
  const getMoreCallback = (): void => {
    log.debug('Get more data');
    getMoreFacetValues(facet.name);
  };

  const samplingPrecision = totalSamples / totalMatches;
  const isDataSampled = samplingPrecision < 1;

  // Header
  const titleText = isDataSampled ? (
    <>
      <span className="bold">
        Estimated
        <sup>*</sup>
      </span>
      {' field values for '}
    </>
  ) : <>Field values for </>;

  const closeButton = (
    <span
      className="FacetDetails__Close"
      onClick={close}
      role="button"
      tabIndex={0}
      aria-label="Close"
      data-test-id={ELEMENT.facetDetailsClose}
    >
      <CloseIcon fontSize="small" />
    </span>
  );

  let titleContent = (
    <div>
      {titleText}
      <ShadedText text={facet.name} />
    </div>
  );
  if (isSourceSelectorView) {
    titleContent = (
      <div>
        <div className="FacetList__Header">{view.sources?.sourceLabelPlural ?? 'Sources'}</div>
      </div>
    );
  }
  const title = (
    <div className="FacetDetails__Title">
      {titleContent}
      {closeButton}
    </div>
  );

  // Buttons
  let items;
  const graphIcon = <GraphIcon fontSize="small" />;
  const distributionIcon = <DistributionIcon fontSize="small" />;

  const graphLink = getLinkForRoute(K.ROUTE.GRAPH, eventParams, { facet: facet.name } as UrlParams);
  const breakdownLink = getLinkForRoute(K.ROUTE.GRAPH, eventParams, { facet: 'rate', breakdownFacet: facet.name } as UrlParams);
  const distributionLink = getLinkForRoute(K.ROUTE.DISTRIBUTION, eventParams, { facet: facet.name } as UrlParams);

  // the link for the Search button adds a term that combines checked values using "in"
  let searchLink = '';
  if (checkedValues.length === 1) {
    searchLink = getLink(facet.name, '=', checkedValues[0]);
  } else if (checkedValues.length > 1) {
    const valueList = checkedValues.join(',');
    searchLink = getLink(facet.name, 'in', '(' + valueList + ')');
  }

  if (checkedValues.length > 0) {
    items = [
      <Button
        href={searchLink}
        onClick={close}
        size="sm"
        disabled={checkedValues.length === 0}
        testId={ELEMENT.facetDetailsGraphMatches}
      >
        {isSourceSelectorView ? 'Apply' : 'Add to Search'}
      </Button>,
    ];
  } else if (facet.isNumeric) {
    items = [
      <Button
        href={graphLink}
        onClick={close}
        size="sm"
        leftIcon={graphIcon}
        testId={ELEMENT.facetDetailsGraphValues}
      >
        Graph Values
      </Button>,
      <Button
        href={breakdownLink}
        onClick={close}
        size="sm"
        testId={ELEMENT.facetDetailsGraphMatches}
      >
        # Matches
      </Button>,
      <Button
        href={distributionLink}
        onClick={close}
        size="sm"
        leftIcon={distributionIcon}
        testId={ELEMENT.facetDetailsDistribution}
      >
        Distribution
      </Button>,
    ];
  } else {
    items = [
      <Button
        href={breakdownLink}
        onClick={close}
        size="sm"
        testId={ELEMENT.facetDetailsGraphMatchesBy}
      >
        Graph # Matches by: &nbsp;
        <span className="FacetDetails__FacetName">{facet.name}</span>
      </Button>,
    ];
  }
  const toolbar = (
    <div className="FacetDetails__Toolbar">
      <Toolbar items={items} />
    </div>
  );

  // Set up headers and data for the facet values table

  // update list of checked values when a "select all" is checked/unchecked
  const onSelectAllCheckboxChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const target = event.target;
    if (target.checked && facet?.values) {
      setCheckedValues(facetValues.map((value) => value.value));
    } else {
      setCheckedValues([]);
    }
  };

  const checkboxHeader = (
    <CheckboxRadio
      type="checkbox"
      checked={checkedValues.length === facetValues.length}
      onChange={onSelectAllCheckboxChange}
      id={ELEMENT.facetDetailsSelectAll}
      testId={ELEMENT.facetDetailsSelectAll}
    />
  );

  // table has 5 columns
  const headers = [
    {
      content: () => checkboxHeader,
      field:   'checkbox',
      width:   30,
    },
    {
      content: isSourceSelectorView && view.sources?.sourceLabel ? view.sources.sourceLabel : 'Value',
      field:   'value',
    },
    {
      content: 'Filter',
      field:   'filter',
      width:   60,
    },
    {
      field: 'histogram',
      width: 120,
    },
    {
      content:   showingMoreValues ? '# Matches' : '# Approx',
      field:     'count',
      width:     110,
      alignment: K.ALIGNMENT.END,
    },
  ];

  // histogram is relative to highest count for any value
  let maxCount = 0;
  facetValues.forEach((valueInfo) => {
    maxCount = Math.max(maxCount, valueInfo.count);
  });

  // set up some metrics that capture the sampling and match rates of this facet
  const digits = samplingPrecision ? Math.floor(-Math.log10(1 - samplingPrecision)) + 1 : 1;
  const estimatedMatches = roundDownToSigFigs(facet.matchCount, digits);
  const matchPercentage = Math.round(100 * (facet.matchCount / totalMatches));
  const precision = samplingPrecision * (facet.sampledMatchCount / facet.matchCount);
  const facetDigits = precision ? Math.floor(-Math.log10(1 - precision)) + 1 : 1;

  // map the data into objects with values for each of our 5 columns

  // update list of checked values when a single value is checked/unchecked
  const onCheckboxChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const target = event.target;
    const newCheckedValues = cloneDeep(checkedValues);
    if (target.checked) {
      newCheckedValues.push(target.value);
    } else {
      const idx = newCheckedValues.indexOf(target.value);
      if (idx !== -1) {
        newCheckedValues.splice(idx, 1);
      }
    }
    setCheckedValues(newCheckedValues);
  };

  const tilde = showingMoreValues ? null : <span className="FacetDetails__Tilde">~</span>;
  const data = facetValues.map((valueInfo, index) => {
    const formattedValue = formatFacetValue(valueInfo.value);
    const tooltipId = createTestId(facet.name, 'tooltip', index);
    const checkboxId = createTestId(facet.name, 'checkbox', index);
    const facetValueTestId = createTestId(ELEMENT.facetValue, index);
    const schema = isSourceSelectorView && findSchema(formattedValue);
    const valueLink = (
      <Link
        className="FacetDetails__Link"
        href={curLink}
        onHover={(): void => setCurLink(getLink(facet.name, '=', valueInfo.value))}
        testId={facetValueTestId}
        onClick={(): void => close()}
      >
        {schema && <DataSourceIcon className="FacetDetails__Icon" schema={schema} />}
        <TruncateWithTooltip
          text={formattedValue}
          maxLength={80}
          id={tooltipId}
          placement={TooltipPlacement.BOTTOM}
        />
      </Link>
    );
    const percentage = maxCount > 0 ? Math.round((valueInfo.count / maxCount) * 100) + '%' : '0%';
    const histogramStyle = { width: percentage };
    const valueCount = showingMoreValues
      ? valueInfo.count
      : addCommas(roundDownToSigFigs(valueInfo.count, facetDigits), 10000);

    return {
      checkbox: (
        <CheckboxRadio
          type="checkbox"
          value={valueInfo.value}
          checked={checkedValues.includes(valueInfo.value)}
          onChange={onCheckboxChange}
          id={checkboxId}
          testId={checkboxId}
        />
      ),
      value: valueLink,
      filter: (
        <FacetFilter
          field={facet.name}
          value={valueInfo.value}
          onSearch={close}
          valueIsNumeric={facet.isNumeric}
          showDisabled={false}
          index={index}
        />
      ),
      histogram: <div className="FacetDetails__Histogram" style={histogramStyle} />,
      count: (
        <div>
          {tilde}
          {' '}
          {valueCount}
        </div>
      ),
    };
  });

  // Table options
  // TODO: do we want it to be sortable?
  const options = {
    isStriped: false,
    isSortable: false,
    hoverRow: true,
    sidePadding: 10,
    size: K.SIZE.SMALL,
    tableClass: 'FacetDetails__Table',
    headerClass: 'FacetDetails__TableHeaderRow',
    headerItemClass: 'FacetDetails__TableHeaderItem',
    rowClass: 'FacetDetails__TableRow',
    itemClass: 'FacetDetails__TableItem',
  };

  // Summary stats at the bottom, and a button to get more/better data

  const preface = isDataSampled ? (
    <span>
      <sup>*</sup>
      Sampling estimates:
    </span>
  ) : (
    <span>Value Summary:</span>
  );

  let matchRateText = '';
  const estimatedMatchesText = addCommas(estimatedMatches, 10000);
  const matches = pluralize('match', estimatedMatches, 'es');
  const totalMatchesText = addCommas(totalMatches, 10000);
  const events = pluralize('event', totalMatches);
  if (matchPercentage === 0) {
    matchRateText = [estimatedMatchesText, matches, '/', totalMatchesText, events, '= <1%'].join(' ');
  } else if (isDataSampled && matchPercentage > 0) {
    matchRateText = [
      estimatedMatchesText + '+',
      matches,
      '/',
      totalMatchesText,
      events,
      '= ~' + matchPercentage + '%',
    ].join(' ');
  } else if (!isDataSampled && matchPercentage > 0) {
    matchRateText = [
      estimatedMatchesText + '+',
      matches,
      '/',
      totalMatchesText,
      events,
      '=',
      matchPercentage + '%',
    ].join(' ');
  }
  const matchRate = matchRateText ? <li>{matchRateText}</li> : null;

  let samplePercentageText = '';
  if (facet.sampledMatchCount != null) {
    const samplePercentage = Math.round(100 * (facet.sampledMatchCount / facet.matchCount));
    if (samplePercentage === 0) {
      samplePercentageText = '<1';
    } else if (samplePercentage < 100) {
      samplePercentageText = '~' + samplePercentage;
    }
  }
  const samplePercentage = samplePercentageText ? (
    <li>
      Sampled
      {' '}
      {samplePercentageText}
      % of matching events
    </li>
  ) : null;

  let showingText = '';
  const values = pluralize('value', facet.uniqueValuesCount);
  if (precision < 1 && !showingMoreValues) {
    showingText =      'Showing '
      + addCommas(facetValues.length)
      + ' of at least '
      + addCommas(facet.uniqueValuesCount)
      + ' unique '
      + values;
  } else {
    showingText =      'Showing ' + addCommas(facetValues.length) + ' of ' + addCommas(facet.uniqueValuesCount) + ' unique ' + values;
  }
  const showing = <li>{showingText}</li>;

  const summary = !isSourceSelectorView && (
    <div className="FacetDetails__Summary">
      <div>{preface}</div>
      <ul>
        {matchRate}
        {samplePercentage}
        {showing}
      </ul>
    </div>
  );

  let moreButton = null;
  if (fetchingMoreValues) {
    moreButton = (
      <div className="FacetDetails__GetMoreButton">
        <LoadingPlaceholder offsetTop={0} height={30} spinnerHeight={30} />
      </div>
    );
  } else if (!showingMoreValues) {
    moreButton = (
      <div className="FacetDetails__GetMoreButton">
        <Button size="sm" onClick={getMoreCallback} testId={ELEMENT.facetDetailsCheckForMore}>
          {fetchingMoreValues ? 'fetching ...' : 'Check for More'}
        </Button>
      </div>
    );
  }

  const facetDetailsClass = classNames('FacetDetails', { 'FacetDetails--SourceSelectorView': isSourceSelectorView });
  return (
    <div className={facetDetailsClass}>
      {title}
      {toolbar}
      <div className="FacetDetails__TableContainer">
        <Table headers={headers} data={data} options={options} />
      </div>
      {summary}
      {moreButton}
    </div>
  );
}

export default FacetDetails;
